import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import GameOverModal from "../../../components/GameOverModal";
import GameWonModal from "../../../components/GameWonModal";
import "../../../assets/css/gamesCss/List/doublyDeletion.css"
import { useDarkMode } from "../../../context/DarkModeProvider";
import { useLanguage } from "context/LanguageProvider";



const DoublyLinkedListDeletionGame = () => {
    const [currentLevel, setCurrentLevel] = useState(0);
    const [nodes, setNodes] = useState([]);
    const [headId, setHeadId] = useState(null);
    const [deletedNodes, setDeletedNodes] = useState([]);
    const [lives, setLives] = useState(3);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [selectedNode, setSelectedNode] = useState(null);
    const [actionMode, setActionMode] = useState(null);
    const [reconnectFrom, setReconnectFrom] = useState(null);
    const [reconnectType, setReconnectType] = useState(null);
    const [showGameOver, setShowGameOver] = useState(false);
    const [showGameWon, setShowGameWon] = useState(false);
    const [timeLeft, setTimeLeft] = useState(25);
    const [isTimerRunning, setIsTimerRunning] = useState(false);
    const timerRef = useRef(null);
    const { selLanguage, toggleLanguage, isLoading } = useLanguage();

    const { darkMode } = useDarkMode();

    const LEVELS = [
        {
            id: 1,
            title: selLanguage
                ? "Επίπεδο 1: Διαγραφή Πρώτου Κόμβου"
                : "Level 1: Delete First Node",
            description: selLanguage
                ? "Διέγραψε τον πρώτο κόμβο της λίστας και δώσε τη νέα της μορφή."
                : "Delete the first node of the list and provide its new form.",
            initialList: [
                { id: "A", value: "10", next: "B", prev: null },
                { id: "B", value: "20", next: "C", prev: "A" },
                { id: "C", value: "30", next: "D", prev: "B" },
                { id: "D", value: "40", next: null, prev: "C" },
            ],
            headId: "A",
            deleteTargets: ["A"],
            expectedHead: "B",
            expectedList: [
                { id: "B", value: "20", next: "C", prev: null },
                { id: "C", value: "30", next: "D", prev: "B" },
                { id: "D", value: "40", next: null, prev: "C" },
            ]
        },
        {
            id: 2,
            title: selLanguage
                ? "Επίπεδο 2: Διαγραφή Τελευταίου Κόμβου"
                : "Level 2: Delete Last Node",
            description: selLanguage
                ? "Διέγραψε τον τελευταίο κόμβο της λίστας."
                : "Delete the last node of the list.",
            initialList: [
                { id: "X", value: "5", next: "Y", prev: null },
                { id: "Y", value: "15", next: "Z", prev: "X" },
                { id: "Z", value: "25", next: "W", prev: "Y" },
                { id: "W", value: "35", next: "V", prev: "Z" },
                { id: "V", value: "45", next: null, prev: "W" },
            ],
            headId: "X",
            deleteTargets: ["V"],
            expectedHead: "X",
            expectedList: [
                { id: "X", value: "5", next: "Y", prev: null },
                { id: "Y", value: "15", next: "Z", prev: "X" },
                { id: "Z", value: "25", next: "W", prev: "Y" },
                { id: "W", value: "35", next: null, prev: "Z" },
            ]
        },
        {
            id: 3,
            title: selLanguage
                ? "Επίπεδο 3: Διαγραφή Ενδιάμεσου Κόμβου"
                : "Level 3: Delete Middle Node",
            description: selLanguage
                ? "Διέγραψε τον κόμβο R και φτιάξε τις νέες συνδέσεις."
                : "Delete node R and fix the new links.",
            initialList: [
                { id: "P", value: "100", next: "Q", prev: null },
                { id: "Q", value: "200", next: "R", prev: "P" },
                { id: "R", value: "300", next: "S", prev: "Q" },
                { id: "S", value: "400", next: "T", prev: "R" },
                { id: "T", value: "500", next: null, prev: "S" },
            ],
            headId: "P",
            deleteTargets: ["R"],
            expectedHead: "P",
            expectedList: [
                { id: "P", value: "100", next: "Q", prev: null },
                { id: "Q", value: "200", next: "S", prev: "P" },
                { id: "S", value: "400", next: "T", prev: "Q" },
                { id: "T", value: "500", next: null, prev: "S" },
            ]
        },
        {
            id: 4,
            title: selLanguage
                ? "Επίπεδο 4: Διπλή Διαγραφή - Πρώτος και Ενδιάμεσος"
                : "Level 4: Double Deletion – First and Middle",
            description: selLanguage
                ? "Διέγραψε τον πρώτο κόμβο και τον κόμβο Ο και φτιάξε τη νέα λίστα!"
                : "Delete the first node and node O and build the new list!",
            initialList: [
                { id: "M", value: "50", next: "N", prev: null },
                { id: "N", value: "60", next: "O", prev: "M" },
                { id: "O", value: "70", next: "L", prev: "N" },
                { id: "L", value: "80", next: "J", prev: "O" },
                { id: "J", value: "90", next: "I", prev: "L" },
                { id: "I", value: "100", next: null, prev: "J" },
            ],
            headId: "M",
            deleteTargets: ["M", "O"],
            expectedHead: "N",
            expectedList: [
                { id: "N", value: "60", next: "L", prev: null },
                { id: "L", value: "80", next: "J", prev: "N" },
                { id: "J", value: "90", next: "I", prev: "L" },
                { id: "I", value: "100", next: null, prev: "J" },
            ]
        },
        {
            id: 5,
            title: selLanguage
                ? "Επίπεδο 5: Τριπλή Διαγραφή - Ultimate Challenge!"
                : "Level 5: Triple Deletion – Ultimate Challenge!",
            description: selLanguage
                ? "Διέγραψε τον πρώτο κόμβο, τον D και τον τελευταίο κόμβο. Πρόσεχε όλες τις πιθανές λεπτομέρειες."
                : "Delete the first node, node D and the last node. Pay attention to every possible detail.",
            initialList: [
                { id: "A", value: "1", next: "B", prev: null },
                { id: "B", value: "2", next: "C", prev: "A" },
                { id: "C", value: "3", next: "D", prev: "B" },
                { id: "D", value: "4", next: "E", prev: "C" },
                { id: "E", value: "5", next: "F", prev: "D" },
                { id: "F", value: "6", next: "G", prev: "E" },
                { id: "G", value: "7", next: null, prev: "F" },
            ],
            headId: "A",
            deleteTargets: ["A", "D", "G"],
            expectedHead: "B",
            expectedList: [
                { id: "B", value: "2", next: "C", prev: null },
                { id: "C", value: "3", next: "E", prev: "B" },
                { id: "E", value: "5", next: "F", prev: "C" },
                { id: "F", value: "6", next: null, prev: "E" },
            ]
        }
    ];


    useEffect(() => {
        loadLevel(currentLevel);
    }, [currentLevel]);

    useEffect(() => {
        if (isTimerRunning && timeLeft > 0) {
            timerRef.current = setInterval(() => {
                setTimeLeft(prev => prev - 1);
            }, 1000);
        } else if (timeLeft === 0) {
            handleTimeOut();
        }

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, [isTimerRunning, timeLeft]);


    const handleTimeOut = () => {
        setIsTimerRunning(false);
        setErrorMessage(selLanguage ? "Ο χρόνος τελείωσε!" : "Time's up!");
        setShowError(true);

        const newLives = lives - 1;
        setLives(newLives);
        if (newLives === 0) {
            setShowGameOver(true);
        } else {
            setTimeout(() => {
                setShowError(false);
                loadLevel(currentLevel);
            }, 2000);
        }
        return;
    };


    const loadLevel = (levelIndex) => {
        const level = LEVELS[levelIndex];
        setNodes(JSON.parse(JSON.stringify(level.initialList)));
        setHeadId(level.headId);
        setDeletedNodes([]);
        setShowSuccess(false);
        setShowError(false);
        setErrorMessage("");
        setSelectedNode(null);
        setActionMode(null);
        setReconnectFrom(null);
        setReconnectType(null);
        setShowGameOver(false);
        setTimeLeft(25);
        setIsTimerRunning(true);
    };

    const resetLevel = () => {
        loadLevel(currentLevel);
    };

    const handleMoveHead = (nodeId) => {
        setHeadId(nodeId);
        setSelectedNode(null);
    };

    const handleDeleteNode = (nodeId) => {
        setDeletedNodes([...deletedNodes, nodeId]);
        setNodes(nodes.filter(n => n.id !== nodeId));

        if (headId === nodeId) {
            setHeadId(null);
        }

        setSelectedNode(null);
    };

    const handleReconnectNext = (fromId, toId) => {
        setNodes(nodes.map(node =>
            node.id === fromId ? { ...node, next: toId } : node
        ));
        setReconnectFrom(null);
        setReconnectType(null);
        setSelectedNode(null);
    };

    const handleReconnectPrev = (fromId, toId) => {
        setNodes(nodes.map(node =>
            node.id === fromId ? { ...node, prev: toId } : node
        ));
        setReconnectFrom(null);
        setReconnectType(null);
        setSelectedNode(null);
    };

    const checkSolution = () => {
        setIsTimerRunning(false);
        const level = LEVELS[currentLevel];
        let newLives = lives;

        // Check head
        if (headId !== level.expectedHead) {
            newLives = lives - 1;
            setErrorMessage(selLanguage ? `Λάθος! Το HEAD πρέπει να δείχνει στον κόμβο ${level.expectedHead}` : `Wrong! HEAD should point to node ${level.expectedHead}`);
            setShowError(true);
            setLives(newLives);

            if (newLives === 0) {
                setShowGameOver(true);
            } else {
                setTimeout(() => {
                    setShowError(false);
                    loadLevel(currentLevel);
                }, 2000);
            }
            return;
        }

        // Check deleted nodes
        const sortedDeleted = [...deletedNodes].sort();
        const sortedExpected = [...level.deleteTargets].sort();

        if (JSON.stringify(sortedDeleted) !== JSON.stringify(sortedExpected)) {
            newLives = lives - 1;
            setErrorMessage(selLanguage ? `Λάθος! Πρέπει να διαγράψεις: ${level.deleteTargets.join(", ")}` : `Wrong! You should delete: ${level.deleteTargets.join(", ")}`);
            setShowError(true);
            setLives(newLives);

            if (newLives === 0) {
                setShowGameOver(true);
            } else {
                setTimeout(() => {
                    setShowError(false);
                    loadLevel(currentLevel);
                }, 2000);
            }
            return;
        }

        // Check remaining list structure
        const currentIds = nodes.map(n => n.id).sort();
        const expectedIds = level.expectedList.map(n => n.id).sort();

        if (JSON.stringify(currentIds) !== JSON.stringify(expectedIds)) {
            newLives = lives - 1;
            setErrorMessage(selLanguage ? "Λάθος! Οι κόμβοι που απομένουν δεν είναι σωστοί" : "Wrong! The remaining nodes are not correct");
            setShowError(true);
            setLives(newLives);

            if (newLives === 0) {
                setShowGameOver(true);
            } else {
                setTimeout(() => {
                    setShowError(false);
                    loadLevel(currentLevel);
                }, 2000);
            }
            return;
        }

        // Check next AND prev connections
        for (const node of nodes) {
            const expected = level.expectedList.find(n => n.id === node.id);
            if (node.next !== expected.next) {
                newLives = lives - 1;
                setErrorMessage(selLanguage ? `Λάθος! Η σύνδεση NEXT του κόμβου ${node.id} δεν είναι σωστή` : `Wrong! NEXT link of node ${node.id} is not correct`);
                setShowError(true);
                setLives(newLives);

                if (newLives === 0) {
                    setShowGameOver(true);
                } else {
                    setTimeout(() => {
                        setShowError(false);
                        loadLevel(currentLevel);
                    }, 2000);
                }
                return;
            }
            if (node.prev !== expected.prev) {
                newLives = lives - 1;
                setErrorMessage(selLanguage ? `Λάθος! Η σύνδεση PREV του κόμβου ${node.id} δεν είναι σωστή` : `Wrong! PREV link of node ${node.id} is not correct`);
                setShowError(true);
                setLives(newLives);

                if (newLives === 0) {
                    setShowGameOver(true);
                } else {
                    setTimeout(() => {
                        setShowError(false);
                        loadLevel(currentLevel);
                    }, 2000);
                }
                return;
            }
        }


        setShowSuccess(true);
        setTimeout(() => {
            setShowSuccess(false);
            if (currentLevel < LEVELS.length - 1) {
                setCurrentLevel(prev => prev + 1);

            } else {
                setShowGameWon(true);
            }
        }, 2500);
    };

    const handleNodeClick = (nodeId) => {
        if (actionMode === 'move-head') {
            handleMoveHead(nodeId);
        } else if (actionMode === 'delete') {
            handleDeleteNode(nodeId);
        } else if (actionMode === 'reconnect-next') {
            if (!reconnectFrom) {
                setReconnectFrom(nodeId);
                setReconnectType('next');
            } else if (reconnectFrom !== nodeId) {
                handleReconnectNext(reconnectFrom, nodeId === 'NULL' ? null : nodeId);
            }
        } else if (actionMode === 'reconnect-prev') {
            if (!reconnectFrom) {
                setReconnectFrom(nodeId);
                setReconnectType('prev');
            } else if (reconnectFrom !== nodeId) {
                handleReconnectPrev(reconnectFrom, nodeId === 'NULL' ? null : nodeId);
            }
        }
    };
    const getNodePosition = (index) => {
        const containerWidth = 1400;
        const baseNodeWidth = 100;
        const baseSpacing = 200;

        // Υπολογισμός scale factor βάσει αριθμού κόμβων
        const totalNodes = nodes.length + 1; // +1 για το NULL
        const requiredWidth = totalNodes * baseSpacing;
        const scaleFactor = requiredWidth > containerWidth ? containerWidth / requiredWidth : 1;

        const scaledSpacing = baseSpacing * scaleFactor;
        const totalWidth = nodes.length * scaledSpacing;
        const startX = (containerWidth - totalWidth) / 2 + 50;

        return {
            x: startX + index * scaledSpacing,
            y: 225,
            scale: scaleFactor
        };
    };

    const level = LEVELS[currentLevel];

    return (
        <div
            className="main-content doublyDelContainer"
            style={{
                background: darkMode ? "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #101724ff 100%)" : "var(--lightModeGameBackground)"
            }}
        >
            <GameOverModal
                isVisible={showGameOver}
                onPlayAgain={() => {
                    setCurrentLevel(0);
                    loadLevel(0);
                    setLives(3);
                    setShowGameOver(false);
                }}
            />

            <GameWonModal
                isVisible={showGameWon}
                onPlayAgain={() => {
                    setCurrentLevel(0);
                    loadLevel(0);
                    setLives(3);
                    setShowGameOver(false);
                    setShowGameWon(false);
                }}
            />

            {/* Header */}
            <div style={{
                width: '100%',
                maxWidth: '900px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '15px'
            }}>
                <div style={{
                    color: darkMode ? "white" : "#1e293b",
                    padding: "12px 24px",
                    borderRadius: "12px",
                    fontWeight: "700",
                    fontSize: "18px",
                    border: darkMode ? "2px solid rgba(255, 255, 255, 0.2)" : "2px solid #1e293b",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px"
                }}>

                    {selLanguage ? "Επίπεδο" : "Level"} {level.id}/{LEVELS.length}
                </div>

                {/* Timer */}
                <div style={{
                    background: timeLeft <= 5 ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    color: 'white',
                    padding: '10px 25px',
                    borderRadius: '15px',
                    fontSize: '24px',
                    fontWeight: 'bold',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                    animation: timeLeft <= 5 ? 'pulse 0.5s ease-in-out infinite' : 'none'
                }}>
                    {timeLeft}s
                </div>

                <div style={{
                    display: "flex",
                    alignItems: "center",
                    flexDirection: "row",
                    justifyContent: "right",
                    gap: "12px"
                }}>


                    <div style={{
                        display: "flex",
                        gap: "6px",
                        alignItems: "center"
                    }}>
                        {[1, 2, 3].map((heartNum) => (
                            <div
                                key={heartNum}
                                style={{
                                    fontSize: "24px",
                                    filter: lives >= heartNum
                                        ? "drop-shadow(0 4px 8px rgba(220, 38, 38, 0.4))"
                                        : "grayscale(100%) opacity(0.3)",
                                    transform: lives >= heartNum ? "scale(1)" : "scale(0.8)",
                                    transition: "all 0.3s ease"
                                }}
                            >
                                ❤️
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Instructions */}
            <div className="doublyDelPron">
                <p>
                    {level.description}
                </p>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button
                    className={`doublyDelButton ${actionMode === 'move-head' ? 'active' : ''}`}
                    onClick={() => setActionMode(actionMode === 'move-head' ? null : 'move-head')}
                    style={{
                        background: darkMode ? "#1E293B" : "var(--customBuildButton)",
                    }}
                >
                    {selLanguage ? "Μετακίνηση HEAD" : "Move HEAD"}
                </button>
                <button
                    className={`doublyDelButton ${actionMode === 'reconnect-next' ? 'active' : ''}`}
                    style={{
                        background: darkMode ? "#1E293B" : "var(--customBuildButton)",
                    }}
                    onClick={() => {
                        setActionMode(actionMode === 'reconnect-next' ? null : 'reconnect-next');
                        setReconnectFrom(null);
                        setReconnectType(null);
                    }}
                >
                    {selLanguage ? "Σύνδεση NEXT →" : "Reconnect NEXT →"}
                </button>
                <button
                    className={`doublyDelButton ${actionMode === 'reconnect-prev' ? 'active' : ''}`}
                    style={{
                        background: darkMode ? "#1E293B" : "var(--customBuildButton)",
                    }}
                    onClick={() => {
                        setActionMode(actionMode === 'reconnect-prev' ? null : 'reconnect-prev');
                        setReconnectFrom(null);
                        setReconnectType(null);
                    }}
                >
                    {selLanguage ? "Σύνδεση ← PREV" : "Reconnect ← PREV"}
                </button>
                <button
                    className={`doublyDelButton ${actionMode === 'delete' ? 'active' : ''}`}
                    style={{
                        background: darkMode ? "#1E293B" : "var(--customBuildButton)",
                    }}
                    onClick={() => setActionMode(actionMode === 'delete' ? null : 'delete')}
                >
                    {selLanguage ? "Διαγραφή Κόμβου" : "Delete Node"}
                </button>
            </div>

            {reconnectFrom && (
                <div style={{
                    background: reconnectType === 'next' ? '#dcfce7' : '#fef3c7',
                    padding: '12px',
                    borderRadius: '8px',
                    textAlign: 'center',
                    marginBottom: '20px',
                    border: reconnectType === 'next' ? '2px solid #10b981' : '2px solid #f59e0b',
                    color: '#1e293b',
                    fontWeight: 'bold'
                }}>
                    {selLanguage ? `Επέλεξε τον κόμβο για ${reconnectType === 'next' ? 'NEXT' : 'PREV'} του ${reconnectFrom}` : `Select node to be the ${reconnectType === 'next' ? 'NEXT' : 'PREV'} of ${reconnectFrom}`}
                </div>
            )}

            {/* Linked List Visualization */}
            <div className="doublyDelVisual">
                <svg width="100%" height="500" viewBox="0 0 1400 500">
                    <defs>
                        <marker
                            id="arrowhead-next"
                            markerWidth="10"
                            markerHeight="10"
                            refX="9"
                            refY="3"
                            orient="auto"
                        >
                            <polygon points="0 0, 10 3, 0 6" fill="#10b981" />
                        </marker>
                        <marker
                            id="arrowhead-prev"
                            markerWidth="10"
                            markerHeight="10"
                            refX="10"
                            refY="3"
                            orient="auto"
                        >
                            <polygon points="10 3, 0 0, 0 6" fill="#f59e0b" />
                        </marker>

                    </defs>

                    {/* Draw NEXT connections (straight arrows, top edge) */}
                    {nodes.map((node, index) => {
                        const pos = getNodePosition(index);

                        const nodeWidth = 100 * pos.scale;
                        const nodeHeight = 70 * pos.scale;
                        const startX = pos.x + nodeWidth;                 // δεξιά πλευρά
                        const startY = pos.y - nodeHeight / 3;            // πάνω πλευρά

                        if (node.next) {
                            const nextNode = nodes.find(n => n.id === node.next);
                            if (nextNode) {
                                const nextIndex = nodes.indexOf(nextNode);
                                const nextPos = getNodePosition(nextIndex);

                                const targetNodeWidth = 100 * nextPos.scale;
                                const targetNodeHeight = 80 * nextPos.scale;
                                const endX = nextPos.x;                   // αριστερή πλευρά του επόμενου
                                const endY = nextPos.y - targetNodeHeight / 3.5; // πάνω πλευρά

                                return (
                                    <motion.line
                                        key={`next-${node.id}`}
                                        x1={startX}
                                        y1={startY}
                                        x2={endX}
                                        y2={endY}
                                        stroke="#10b981"
                                        strokeWidth="3"
                                        markerEnd="url(#arrowhead-next)"
                                        initial={{ pathLength: 0 }}
                                        animate={{ pathLength: 1 }}
                                        transition={{ duration: 0.5 }}
                                    />
                                );
                            }
                        } else {
                            const nullPos = getNodePosition(nodes.length);
                            const endX = nullPos.x;
                            const endY = nullPos.y - (80 * nullPos.scale) / 7; // περίπου στην πάνω μεριά του NULL box

                            return (
                                <motion.line
                                    key={`next-${node.id}-null`}
                                    x1={startX}
                                    y1={startY}
                                    x2={endX}
                                    y2={endY}
                                    stroke="#10b981"
                                    strokeWidth="3"
                                    strokeDasharray="5,5"
                                    markerEnd="url(#arrowhead-next)"
                                    initial={{ pathLength: 0 }}
                                    animate={{ pathLength: 1 }}
                                    transition={{ duration: 0.5 }}
                                />
                            );
                        }
                        return null;
                    })}




                    {/* Draw PREV connections (straight arrows, from node to previous on bottom edge) */}
                    {nodes.map((node, index) => {
                        const pos = getNodePosition(index);

                        const nodeWidth = 100 * pos.scale;
                        const nodeHeight = 80 * pos.scale;

                        // ξεκίνημα από την ΚΑΤΩ ΑΡΙΣΤΕΡΗ γωνία του ΤΡΕΧΟΝΤΟΣ κόμβου
                        const startX = pos.x + nodeWidth * 0.1;
                        const startY = pos.y + nodeHeight / 2;

                        if (node.prev) {
                            const prevNode = nodes.find(n => n.id === node.prev);
                            if (prevNode) {
                                const prevIndex = nodes.indexOf(prevNode);
                                const prevPos = getNodePosition(prevIndex);

                                const prevNodeWidth = 100 * prevPos.scale;
                                const prevNodeHeight = 80 * prevPos.scale;

                                // τερματισμός στην ΚΑΤΩ ΔΕΞΙΑ γωνία του ΠΡΟΗΓΟΥΜΕΝΟΥ κόμβου
                                const endX = prevPos.x + prevNodeWidth + 0;
                                const endY = prevPos.y + prevNodeHeight / 2;

                                return (
                                    <motion.line
                                        key={`prev-${node.id}`}
                                        x1={startX}
                                        y1={startY}
                                        x2={endX}
                                        y2={endY}
                                        stroke="#f59e0b"
                                        strokeWidth="3"
                                        markerEnd="url(#arrowhead-prev)"
                                        initial={{ pathLength: 0 }}
                                        animate={{ pathLength: 1 }}
                                        transition={{ duration: 0.5 }}
                                    />
                                );
                            }
                        } else {
                            // σύνδεση με το NULL box στην αρχή (αριστερά)
                            const firstPos = getNodePosition(0);
                            const scale = firstPos.scale;
                            const boxWidth = 50 * scale;
                            const boxHeight = 30 * scale;
                            const boxX = firstPos.x - 120 * scale;

                            const endX = boxX + boxWidth;      // δεξιά πλευρά του NULL box
                            const endY = firstPos.y + boxHeight / 2;

                            return (
                                <motion.line
                                    key={`prev-${node.id}-null`}
                                    x1={startX}
                                    y1={startY}
                                    x2={endX}
                                    y2={endY}
                                    stroke="#f59e0b"
                                    strokeWidth="3"
                                    strokeDasharray="5,5"
                                    markerEnd="url(#arrowhead-prev)"
                                    initial={{ pathLength: 0 }}
                                    animate={{ pathLength: 1 }}
                                    transition={{ duration: 0.5 }}
                                />
                            );
                        }

                        return null;
                    })}


                    {/* Draw HEAD indicator */}
                    {headId && nodes.find(n => n.id === headId) && (
                        <motion.g
                            initial={{ y: -20, opacity: 0 }}
                            animate={{ y: -15, opacity: 1 }}
                        >
                            <text
                                x={getNodePosition(nodes.findIndex(n => n.id === headId)).x + 30}
                                y={getNodePosition(nodes.findIndex(n => n.id === headId)).y - 80}
                                textAnchor="middle"
                                fill="#6366f1"
                                fontSize="20"
                                fontWeight="700"
                            >
                                HEAD
                            </text>
                            <line
                                x1={getNodePosition(nodes.findIndex(n => n.id === headId)).x + 30}
                                y1={getNodePosition(nodes.findIndex(n => n.id === headId)).y - 70}
                                x2={getNodePosition(nodes.findIndex(n => n.id === headId)).x + 30}
                                y2={getNodePosition(nodes.findIndex(n => n.id === headId)).y - 35}
                                stroke="#6366f1"
                                strokeWidth="3"
                                markerEnd="url(#arrowhead-next)"
                            />
                        </motion.g>
                    )}

                    {/* HEAD indicator - if no headId, point to start NULL */}
                    {!headId && nodes.length > 0 && (
                        <motion.g
                            initial={{ y: -20, opacity: 0 }}
                            animate={{ y: -15, opacity: 1 }}
                        >
                            <text
                                x={getNodePosition(0).x - 95}
                                y={getNodePosition(0).y - 80}
                                textAnchor="middle"
                                fill="#6366f1"
                                fontSize="20"
                                fontWeight="700"
                            >
                                HEAD
                            </text>
                            <line
                                x1={getNodePosition(0).x - 95}
                                y1={getNodePosition(0).y - 70}
                                x2={getNodePosition(0).x - 95}
                                y2={getNodePosition(0).y - 35}
                                stroke="#6366f1"
                                strokeWidth="3"
                                markerEnd="url(#arrowhead-next)"
                            />
                        </motion.g>
                    )}



                    {/* Draw nodes */}
                    {nodes.map((node, index) => {
                        const pos = getNodePosition(index);
                        const nodeWidth = 100 * pos.scale;
                        const nodeHeight = 80 * pos.scale;

                        return (
                            <motion.g
                                key={node.id}
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <rect
                                    x={pos.x}
                                    y={pos.y - (nodeHeight / 2)}
                                    width={nodeWidth}
                                    height={nodeHeight}
                                    fill="#eff6ff"
                                    stroke="#3b82f6"
                                    strokeWidth={4 * pos.scale}
                                    rx={10 * pos.scale}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handleNodeClick(node.id)}
                                />
                                <text
                                    x={pos.x + nodeWidth / 2}
                                    y={pos.y - 5 * pos.scale}
                                    textAnchor="middle"
                                    fill="#1e293b"
                                    fontSize={20 * pos.scale}
                                    fontWeight="700"
                                    style={{ pointerEvents: 'none' }}
                                >
                                    {node.id}
                                </text>
                                <text
                                    x={pos.x + nodeWidth / 2}
                                    y={pos.y + 20 * pos.scale}
                                    textAnchor="middle"
                                    fill="#64748b"
                                    fontSize={16 * pos.scale}
                                    style={{ pointerEvents: 'none' }}
                                >
                                    {node.value}
                                </text>

                                {/* Next pointer indicator (top right) */}
                                <circle
                                    cx={pos.x + nodeWidth * 0.9}
                                    cy={pos.y - nodeHeight / 2 + 15 * pos.scale}
                                    r={6 * pos.scale}
                                    fill="#10b981"
                                />


                                {/* Prev pointer indicator (bottom left) */}
                                <circle
                                    cx={pos.x + nodeWidth * 0.1}
                                    cy={pos.y + nodeHeight / 2 - 15 * pos.scale}
                                    r={6 * pos.scale}
                                    fill="#f59e0b"
                                />

                            </motion.g>
                        );
                    })}

                    {/* NULL box */}
                    {nodes.length > 0 && (() => {
                        const nullPos = getNodePosition(nodes.length);
                        const boxWidth = 50 * nullPos.scale;
                        const boxHeight = 30 * nullPos.scale;

                        return (
                            <g>
                                <rect
                                    x={nullPos.x}
                                    y={nullPos.y - boxHeight / 2}
                                    width={boxWidth}
                                    height={boxHeight}
                                    fill="#1f2937"
                                    stroke="#64748b"
                                    strokeWidth={2 * nullPos.scale}
                                    rx={5 * nullPos.scale}
                                    style={{ cursor: (actionMode === 'reconnect-next' || actionMode === 'reconnect-prev') && reconnectFrom ? 'pointer' : 'default' }}
                                    onClick={() => (actionMode === 'reconnect-next' || actionMode === 'reconnect-prev') && reconnectFrom && handleNodeClick('NULL')}
                                />
                                <text
                                    x={nullPos.x + boxWidth / 2}
                                    y={nullPos.y + 5 * nullPos.scale}
                                    textAnchor="middle"
                                    fill="#94a3b8"
                                    fontSize={14 * nullPos.scale}
                                    fontWeight="700"
                                    style={{ pointerEvents: 'none' }}
                                >
                                    NULL
                                </text>
                            </g>
                        );
                    })()}

                    {/* NULL box at the start (for prev connections) */}
                    {nodes.length > 0 && (() => {
                        const firstPos = getNodePosition(0);
                        const boxWidth = 50 * firstPos.scale;
                        const boxHeight = 30 * firstPos.scale;
                        const boxX = firstPos.x - 120 * firstPos.scale;

                        return (
                            <g>
                                <rect
                                    x={boxX}
                                    y={firstPos.y - boxHeight / 2}
                                    width={boxWidth}
                                    height={boxHeight}
                                    fill="#1f2937"
                                    stroke="#64748b"
                                    strokeWidth={2 * firstPos.scale}
                                    rx={5 * firstPos.scale}
                                    style={{ cursor: (actionMode === 'reconnect-prev') && reconnectFrom ? 'pointer' : 'default' }}
                                    onClick={() => (actionMode === 'reconnect-prev') && reconnectFrom && handleNodeClick('NULL')}
                                />
                                <text
                                    x={boxX + boxWidth / 2}
                                    y={firstPos.y + 5 * firstPos.scale}
                                    textAnchor="middle"
                                    fill="#94a3b8"
                                    fontSize={14 * firstPos.scale}
                                    fontWeight="700"
                                    style={{ pointerEvents: 'none' }}
                                >
                                    NULL
                                </text>
                            </g>
                        );
                    })()}
                </svg>

                {/* Legend */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '30px',
                    marginTop: '20px',
                    fontSize: '14px',
                    fontWeight: 'bold'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '30px', height: '3px', background: '#10b981' }}></div>
                        <span style={{ color: '#10b981' }}>NEXT →</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '30px', height: '3px', background: '#f59e0b' }}></div>
                        <span style={{ color: '#f59e0b' }}>← PREV</span>
                    </div>
                </div>
            </div>


            {/* Error Message */}
            {showError && (
                <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    style={{
                        background: '#fee2e2',
                        borderRadius: '15px',
                        padding: '20px',
                        marginBottom: '20px',
                        border: '2px solid #dc2626',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: "95%"
                    }}
                >

                    <p style={{ color: '#991b1b', margin: 0 }}>
                        {errorMessage}
                    </p>
                </motion.div>
            )}

            {/* Success Message */}
            {showSuccess && (
                <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    style={{
                        background: '#d1fae5',
                        borderRadius: '15px',
                        padding: '20px',
                        marginBottom: '20px',
                        border: '2px solid #10b981',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: "95%"
                    }}
                >
                    <p style={{ color: '#065f46', margin: 0 }}>
                        {selLanguage ? "Συγχαρητήρια! Προχωράς στο επόμενο επίπεδο!" : "Congratulations! Proceeding to the next level!"}
                    </p>
                </motion.div>
            )}



            {/* Deleted Nodes Section */}
            {deletedNodes.length > 0 && (
                <div className="doublyDelNodesSpace">
                    <h3 style={{ color: '#dc2626', marginBottom: '15px' }}>{selLanguage ? "Διαγραμμένοι Κόμβοι" : "Deleted Nodes"}</h3>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        {deletedNodes.map((nodeId, index) => (
                            <motion.div
                                key={nodeId}
                                style={{
                                    background: '#dc2626',
                                    color: 'white',
                                    padding: '10px 20px',
                                    borderRadius: '8px',
                                    fontWeight: 'bold',
                                    fontSize: '16px'
                                }}
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ delay: index * 0.1 }}
                            >
                                {nodeId}
                            </motion.div>
                        ))}
                    </div>
                </div>
            )}



            {/* Control Buttons */}
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button
                    onClick={checkSolution}
                    className="doublyDelCheckButton"
                    onMouseEnter={(e) => e.target.style.transform = "scale(1.05)"}
                    onMouseLeave={(e) => e.target.style.transform = "scale(1)"}
                >
                    {selLanguage ? "Έλεγχος Λύσης" : "Check Solution"}
                </button>
                <button
                    onClick={resetLevel}
                    className="doublyDelDeletionButton"
                    onMouseEnter={(e) => e.target.style.transform = "scale(1.05)"}
                    onMouseLeave={(e) => e.target.style.transform = "scale(1)"}
                >
                    {selLanguage ? "Επαναφορά" : "Reset Level"}
                </button>
            </div>

            <style>{`
                @keyframes pulse {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.1); }
                }
            `}</style>
        </div>
    );
};

export default DoublyLinkedListDeletionGame;